import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/** @type {Promise<void> | null} */
let pendingUpdate = null;

/**
 * A custom element that adds or removes the gift wrap product in the cart.
 * Keeps the gift wrap at a quantity of at most 1 and removes it when it is the only item left.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} giftWrapInput - The checkbox.
 *
 * @extends {Component<Refs>}
 */
class CartGiftWrap extends Component {
  requiredRefs = ['giftWrapInput'];

  // Re-rendering the cart morphs these attributes onto this element
  static observedAttributes = ['data-quantity', 'data-only-item'];

  #ready = false;

  connectedCallback() {
    super.connectedCallback();

    // Wait until the cart components listen for cart updates, so they re-render after a correction
    const listeners = ['cart-items-component', 'cart-icon'].filter((name) => document.querySelector(name));
    Promise.all(listeners.map((name) => customElements.whenDefined(name))).then(() => {
      this.#ready = true;
      this.#enforceRules();
    });
  }

  attributeChangedCallback() {
    if (this.#ready) queueMicrotask(() => this.#enforceRules());
  }

  /**
   * Handles the checkbox change.
   */
  toggle = () => {
    const { giftWrapInput } = this.refs;

    if (pendingUpdate) {
      giftWrapInput.checked = !giftWrapInput.checked;
      return;
    }

    this.#setQuantity(giftWrapInput.checked ? 1 : 0);
  };

  #enforceRules() {
    if (!this.isConnected) return;

    const quantity = Number(this.dataset.quantity);

    if (quantity > 0 && this.hasAttribute('data-only-item')) {
      this.#setQuantity(0);
    } else if (quantity > 1) {
      this.#setQuantity(1);
    }
  }

  /**
   * Sets the gift wrap quantity in the cart and re-renders the cart sections.
   * @param {number} quantity - 0 or 1.
   */
  #setQuantity(quantity) {
    const { variantId, lineKey } = this.dataset;
    if (!variantId || pendingUpdate) return;

    const { giftWrapInput } = this.refs;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      lineKey
        ? new CartLinesUpdateEvent({
            action: quantity > 0 ? 'update' : 'remove',
            context: 'cart',
            lines: [{ id: lineKey, quantity }],
            promise: deferredPromise.promise,
          })
        : new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferredPromise.promise,
          })
    );

    this.classList.add('is-loading');

    pendingUpdate = (async () => {
      try {
        const response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', {
            body: JSON.stringify({
              updates: { [lineKey || variantId]: quantity },
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          })
        );
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Failed to update gift wrap');
        }

        deferredPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
          detail: {
            sections: data.sections,
            items: data.items,
            itemCount: data.item_count,
            source: 'cart-gift-wrap-component',
            didError: false,
          },
        });
      } catch (error) {
        deferredPromise.reject(error);
        giftWrapInput.checked = Number(this.dataset.quantity) > 0;

        this.dispatchEvent(
          new CartErrorEvent({
            error: error instanceof Error ? error.message : 'Failed to update gift wrap',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      } finally {
        this.classList.remove('is-loading');
        pendingUpdate = null;
      }
    })();
  }
}

if (!customElements.get('cart-gift-wrap-component')) {
  customElements.define('cart-gift-wrap-component', CartGiftWrap);
}
